(() => {
  const init = (card) => {
    if (!card || card.__kowiInit) return;
    card.__kowiInit = true;

    const track = card.querySelector(".card-545__track");
    const slides = card.querySelectorAll(".card-545__slide");
    const prev = card.querySelector(".card-545__nav--prev");
    const next = card.querySelector(".card-545__nav--next");
    const progress = card.querySelector(".card-545__progress span");

    if (!track || slides.length <= 1) {
      card.classList.add("is-single");
      return;
    }

    const hoverQuery = window.matchMedia("(min-width: 990px)");
    const mobileQuery = window.matchMedia("(max-width: 989px)");
    let mobileIndex = 0;

    const clampIndex = (index) => Math.max(0, Math.min(slides.length - 1, index));
    const isMobileSlider = () => mobileQuery.matches;

    const applyMobileTransform = (index, options = {}) => {
      mobileIndex = clampIndex(index);

      if (!isMobileSlider()) return;

      card.classList.add("is-mobile-slider");
      card.classList.toggle("is-dragging", options.dragging === true);
      track.style.transition = options.animated === false || options.dragging === true ? "none" : "";
      track.style.setProperty("--card-545-slide-index", String(mobileIndex));
      track.style.setProperty("--card-545-slide-offset", `${mobileIndex * -100}%`);
      track.style.setProperty("--card-545-drag-offset", `${options.dragOffset || 0}px`);
      track.scrollLeft = 0;
    };

    const animateScrollTo = (targetLeft, duration = 420) => {
      const startLeft = track.scrollLeft;
      const distance = targetLeft - startLeft;
      const startTime = performance.now();

      const easeInOutCubic = (t) =>
        t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

      const step = (now) => {
        if (!track.isConnected) return;
        const elapsed = now - startTime;
        const progress = Math.min(1, elapsed / duration);
        const eased = easeInOutCubic(progress);
        track.scrollLeft = startLeft + distance * eased;
        if (progress < 1) requestAnimationFrame(step);
      };

      requestAnimationFrame(step);
    };

    const scrollToIndex = (index, opts = {}) => {
      if (isMobileSlider()) {
        applyMobileTransform(index, { animated: opts.duration !== 0 });
        updateProgress();
        updateArrows();
        showBar();
        return;
      }

      const slide = slides[index];
      if (!slide) return;
      if (opts.duration) {
        animateScrollTo(slide.offsetLeft, opts.duration);
        return;
      }
      track.scrollTo({ left: slide.offsetLeft, behavior: "smooth" });
    };

    const currentIndex = () => {
      if (isMobileSlider()) return mobileIndex;
      const width = track.clientWidth;
      if (!width) return 0;
      return Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / width)));
    };

    const updateArrows = () => {
      const index = currentIndex();
      if (prev) {
        const disabledPrev = index <= 0;
        prev.classList.toggle("is-disabled", disabledPrev);
        prev.toggleAttribute("disabled", disabledPrev);
        prev.setAttribute("aria-disabled", disabledPrev ? "true" : "false");
      }
      if (next) {
        const disabledNext = index >= slides.length - 1;
        next.classList.toggle("is-disabled", disabledNext);
        next.toggleAttribute("disabled", disabledNext);
        next.setAttribute("aria-disabled", disabledNext ? "true" : "false");
      }
    };

    prev?.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (prev.classList.contains("is-disabled")) return;
      scrollToIndex(Math.max(0, currentIndex() - 1));
    });

    next?.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (next.classList.contains("is-disabled")) return;
      scrollToIndex(Math.min(slides.length - 1, currentIndex() + 1));
    });

    let hoverTimer;
    const onHoverEnter = () => {
      if (!hoverQuery.matches) return;
      card.classList.add("is-hover");
      window.clearTimeout(hoverTimer);
      hoverTimer = window.setTimeout(() => {
        scrollToIndex(1, { duration: 520 });
      }, 120);
    };
    const onHoverLeave = () => {
      if (!hoverQuery.matches) return;
      card.classList.remove("is-hover");
      window.clearTimeout(hoverTimer);
      scrollToIndex(0, { duration: 520 });
    };
    card.addEventListener("mouseenter", onHoverEnter);
    card.addEventListener("mouseleave", onHoverLeave);

    let touchStartX = 0;
    let touchStartY = 0;
    let touchStartIndex = 0;
    let touchDeltaX = 0;
    let touchLock = null;
    let didSwipe = false;
    let suppressClickUntil = 0;

    track.addEventListener("touchstart", (event) => {
      if (hoverQuery.matches || event.touches.length !== 1) return;
      const touch = event.touches[0];
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
      touchStartIndex = currentIndex();
      touchDeltaX = 0;
      touchLock = null;
      didSwipe = false;
      applyMobileTransform(touchStartIndex, { animated: false });
    }, { passive: true });

    track.addEventListener("touchmove", (event) => {
      if (hoverQuery.matches || event.touches.length !== 1) return;
      const touch = event.touches[0];
      const deltaX = touchStartX - touch.clientX;
      const deltaY = touchStartY - touch.clientY;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      if (!touchLock && (absX > 6 || absY > 6)) {
        touchLock = absX > absY ? "x" : "y";
      }

      if (touchLock !== "x") return;

      event.preventDefault();
      didSwipe = true;
      touchDeltaX = touch.clientX - touchStartX;
      applyMobileTransform(touchStartIndex, {
        animated: false,
        dragging: true,
        dragOffset: touchDeltaX
      });
      updateProgress();
      updateArrows();
      showBar();
    }, { passive: false });

    track.addEventListener("touchend", () => {
      if (hoverQuery.matches || touchLock !== "x") return;
      const threshold = Math.min(72, Math.max(24, track.clientWidth * 0.18));
      let targetIndex = touchStartIndex;

      if (Math.abs(touchDeltaX) >= threshold) {
        targetIndex = touchStartIndex + (touchDeltaX < 0 ? 1 : -1);
      }

      targetIndex = Math.max(0, Math.min(slides.length - 1, targetIndex));
      suppressClickUntil = Date.now() + 450;
      scrollToIndex(targetIndex, { duration: 360 });
      window.setTimeout(() => {
        didSwipe = false;
      }, 450);
    }, { passive: true });

    track.addEventListener("touchcancel", () => {
      touchLock = null;
      didSwipe = false;
      applyMobileTransform(mobileIndex, { animated: true });
    }, { passive: true });

    track.addEventListener("click", (event) => {
      if (!didSwipe && Date.now() > suppressClickUntil) return;
      event.preventDefault();
      event.stopPropagation();
      didSwipe = false;
    }, true);

    let hideTimer;
    const updateProgress = () => {
      if (!progress) return;
      const index = currentIndex();
      const pct = (index + 1) / slides.length;
      progress.style.transform = `scaleX(${pct})`;
    };

    const showBar = () => {
      card.classList.add("is-scrolling");
      window.clearTimeout(hideTimer);
      hideTimer = window.setTimeout(() => card.classList.remove("is-scrolling"), 900);
    };

    const syncMobileSliderMode = () => {
      if (isMobileSlider()) {
        applyMobileTransform(mobileIndex, { animated: false });
      } else {
        card.classList.remove("is-mobile-slider", "is-dragging");
        track.style.removeProperty("--card-545-slide-index");
        track.style.removeProperty("--card-545-slide-offset");
        track.style.removeProperty("--card-545-drag-offset");
        track.style.removeProperty("transition");
      }

      updateProgress();
      updateArrows();
    };

    track.addEventListener("scroll", () => {
      if (isMobileSlider()) return;
      updateProgress();
      updateArrows();
      showBar();
    }, { passive: true });

    if (typeof mobileQuery.addEventListener === "function") {
      mobileQuery.addEventListener("change", syncMobileSliderMode);
    } else if (typeof mobileQuery.addListener === "function") {
      mobileQuery.addListener(syncMobileSliderMode);
    }

    syncMobileSliderMode();
  };

  let initFrame = null;
  const initAll = (root = document) => {
    const cards = [];
    if (root.matches?.(".card-545")) cards.push(root);
    root.querySelectorAll?.(".card-545").forEach((card) => cards.push(card));
    cards.forEach(init);
  };

  const scheduleInit = (root = document) => {
    if (initFrame) cancelAnimationFrame(initFrame);
    initFrame = requestAnimationFrame(() => {
      initFrame = null;
      initAll(root);
    });
  };

  const nodeHasCards = (node) => {
    if (!node || node.nodeType !== Node.ELEMENT_NODE) return false;
    return node.matches?.(".card-545, #ProductGridContainer") || !!node.querySelector?.(".card-545");
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll());
  } else {
    initAll();
  }

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (nodeHasCards(node)) {
          scheduleInit(node);
          return;
        }
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });

  document.addEventListener("facets:rendered", () => {
    scheduleInit(document.getElementById("ProductGridContainer") || document);
  });
})();
